import {
  getConstructorInstanceClientSide,
  getConstructorInstanceServerSide,
} from './createConstructorInstances'
import {
  ConstructorFacetSdkResponse,
  ConstructorGetAutocompleteParams,
  ConstructorGetAutocompleteResponse,
  ConstructorGetDataParams,
  ConstructorGetDataResponse,
  ConstructorGetFacetsParams,
  ConstructorGetFacetsResponse,
  ConstructorGetRecommendationsParams,
  ConstructorGetRecommendationsResponse,
  ConstructorRequestSdkResponse,
  ConstructorResponseSdkResponse,
  ConstructorResultsSdkResponse,
} from '../../constructorTypes'
import { slugify } from 'lib/slugify'

export async function constructorGetData({
  query = '',
  page = 1,
  resultsPerPage = 100,
  filters = {},
  sortBy,
  sortOrder,
  selectedCategory = 'all',
  isServerSide,
  userId,
}: ConstructorGetDataParams): Promise<ConstructorGetDataResponse> {
  try {
    const constructorio =
      isServerSide && typeof window === 'undefined'
        ? getConstructorInstanceServerSide({ userId })
        : getConstructorInstanceClientSide({ userId })

    const response =
      query === ''
        ? await constructorio.browse.getBrowseResults('group_id', slugify(selectedCategory), {
            page,
            resultsPerPage,
            filters,
            sortBy,
            sortOrder,
          })
        : await constructorio.search.getSearchResults(query, {
            page,
            resultsPerPage,
            filters,
            sortBy,
            sortOrder,
            // preFilterExpression: { // Nice example of how to use AND filters if necessary, we don't need it for now, but just in case
            //   and: [
            //     {
            //       name: 'group_id',
            //       value: 'prints',
            //     },
            //     // {
            //     //   name: 'group_id',
            //     //   value: 'paintings',
            //     // },
            //   ],
            // },
          })
    // console.log('Constructor Response data', response)

    if ('redirect' in response.response) {
      return {
        type: 'redirect',
        redirect: response.response.redirect,
      }
    }

    const requestFromConstructor: ConstructorRequestSdkResponse = response.request
    const responseFromConstructor: ConstructorResponseSdkResponse = response.response
    const dataFromConstructor: ConstructorResultsSdkResponse = responseFromConstructor.results
    const facetsFromConstructor: ConstructorFacetSdkResponse = responseFromConstructor.facets
    const totalNumResultsFromConstructor: number =
      responseFromConstructor?.result_sources?.token_match?.count || 0

    return {
      type: 'products',
      request: requestFromConstructor,
      results: dataFromConstructor,
      facets: facetsFromConstructor,
      totalResults: totalNumResultsFromConstructor,
    }
  } catch (error) {
    console.error('Constructor.io data error:', error)
    return {
      type: 'error',
      error: { message: 'Error getting data from Constructor' },
      totalResults: 0,
    }
  }
}

export async function constructorGetFacets({
  query = '',
  page = 1,
  resultsPerPage = 100,
  filters = {},
  sortBy,
  sortOrder = 'descending',
  selectedCategory = 'all',
  isServerSide,
  userId,
}: ConstructorGetFacetsParams): Promise<ConstructorGetFacetsResponse> {
  try {
    const constructorio =
      isServerSide && typeof window === 'undefined'
        ? getConstructorInstanceServerSide({ userId })
        : getConstructorInstanceClientSide({ userId })

    const response =
      query === ''
        ? await constructorio.browse.getBrowseResults('group_id', slugify(selectedCategory), {
            page,
            resultsPerPage,
            filters,
            sortBy,
            sortOrder,
          })
        : await constructorio.search.getSearchResults(query, {
            page,
            resultsPerPage,
            filters,
            sortBy,
            sortOrder,
          })
    // console.log('Results Facets:', response)

    // const facetsFromConstructor: ConstructorFacetsResponse = getMockFacets()
    const facetsFromConstructor: ConstructorGetFacetsResponse =
      'facets' in response.response ? response.response?.facets : []

    return facetsFromConstructor
  } catch (error) {
    console.error('Constructor.io facets error:', error)
    return []
  }
}

export async function constructorGetAutocomplete({
  query = '',
  filters = {},
  numResults,
  isServerSide,
  userId,
}: ConstructorGetAutocompleteParams): Promise<ConstructorGetAutocompleteResponse> {
  try {
    const constructorio =
      isServerSide && typeof window === 'undefined'
        ? getConstructorInstanceServerSide({ userId })
        : getConstructorInstanceClientSide({ userId })

    const response = await constructorio.autocomplete.getAutocompleteResults(query, {
      filters,
      numResults,
    })
    // console.log(`Autocomplete for ${query} :`, response)

    return {
      request: response.request,
      sections: response.sections,
      totalResultsPerSection: response.total_num_results_per_section,
    }
  } catch (error) {
    console.error('Constructor.io data error:', error)
    return {
      request: null,
      sections: {},
      totalResultsPerSection: {},
    }
  }
}

export async function constructorGetRecommendations<T>({
  podId = '',
  filters = {},
  numResults,
  section = 'Products',
  isServerSide,
  userId,
}: ConstructorGetRecommendationsParams): Promise<ConstructorGetRecommendationsResponse<T>> {
  try {
    const constructorio =
      isServerSide && typeof window === 'undefined'
        ? getConstructorInstanceServerSide({ userId })
        : getConstructorInstanceClientSide({ userId })

    const response = await constructorio.recommendations.getRecommendations(podId, {
      numResults,
      filters,
      section,
    })
    // console.log(`Recommendations for ${podId}:`, response)

    return {
      request: response.request,
      response: response.response,
      totalResults: response.response?.total_num_results || 0,
      resultId: response?.result_id || '',
    }
  } catch (error) {
    console.error('Constructor.io data error:', error)
    return {
      request: null,
      response: null,
      totalResults: 0,
      resultId: '',
    }
  }
}
